// Marketing-landing entry (public/index.html → public/landing.js). Its ONLY job is the optional Clerk
// sign-in: when CLERK_ENABLED is false (default) esbuild dead-code-eliminates the whole thing, so
// landing.js is a no-op and the "Sign in" / "Start free" links keep going straight to /app (Access).
//
// When enabled, it loads Clerk's hosted clerk.browser.js and turns those links into a Clerk sign-in
// modal that returns the user to /app on success. Fail-open: if Clerk can't load, the links still work.

import { CLERK_ENABLED, CLERK_PUBLISHABLE_KEY, loadClerk, type ClerkGlobal } from './lib/clerkConfig.js';

const SIGNIN_SELECTOR = '.nav-signin, .js-start';

if (CLERK_ENABLED) initClerkSignIn();
initDiagCard();
initSlideshow('heroSlides', 'heroDots');
initSlideshow('evSlides', 'evDots');
initNavContrast();
initBeacon();
initSuperRep();
initOrbitHero();

// First-party analytics beacon: fire-and-forget events (page view, CTA clicks, scroll-depth milestones)
// to the same-origin /ev worker route. No cookies; a per-tab random session id in sessionStorage.
// Best-effort — on the *.pages.dev preview (which has no /ev worker route) sendBeacon just fails
// silently, and analytics must never throw into the page.
function initBeacon(): void {
  const nav = navigator as Navigator & { sendBeacon?: (url: string, data?: BodyInit) => boolean };
  let session = '';
  try {
    session = sessionStorage.getItem('sm_sid') || '';
    if (!session) {
      session = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()).slice(2);
      sessionStorage.setItem('sm_sid', session);
    }
  } catch {
    /* private mode / storage blocked — carry on without a session id */
  }

  const track = (name: string, props?: Record<string, unknown>): void => {
    try {
      const body = JSON.stringify({ name, props, path: location.pathname, session });
      if (nav.sendBeacon) {
        nav.sendBeacon('/ev', new Blob([body], { type: 'application/json' }));
      } else {
        void fetch('/ev', {
          method: 'POST',
          body,
          keepalive: true,
          headers: { 'content-type': 'application/json' },
        }).catch(() => {});
      }
    } catch {
      /* never let analytics throw */
    }
  };

  track('page_view');

  // CTA clicks. Passive, non-capturing: never interferes with the Clerk sign-in handler (capture phase).
  document.addEventListener(
    'click',
    (e) => {
      const el = (e.target as HTMLElement | null)?.closest?.('.js-start, .btn-primary, .nav-signin') as HTMLElement | null;
      if (!el) return;
      const label = (el.textContent || '').trim().slice(0, 40);
      const where = (el.closest('section, header')?.className || '').split(' ')[0] || 'page';
      track('cta_click', { label, where, signup: el.classList.contains('js-start') });
    },
    { passive: true },
  );

  // Scroll-depth milestones, once each.
  const marks = [25, 50, 75, 100];
  const hit = new Set<number>();
  const onScroll = () => {
    const de = document.documentElement;
    const max = de.scrollHeight - de.clientHeight;
    if (max <= 0) return;
    const pct = Math.min(100, Math.round((de.scrollTop / max) * 100));
    for (const m of marks) {
      if (pct >= m && !hit.has(m)) {
        hit.add(m);
        track('scroll_depth', { depth: m });
      }
    }
    if (hit.size === marks.length) window.removeEventListener('scroll', onScroll);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
}

// Super-rep flyby: the caped top rep glides across BEHIND the hero copy, then loops. Each pass picks a
// random direction (left->right or right->left), a random height band, and a random speed, with a short
// random gap before the next pass. The rep image is flipped to face its travel direction. Smooth (linear)
// horizontal glide; the gentle bob is a separate continuous CSS animation on .sr-bob. Hidden and never
// started under prefers-reduced-motion.
function initSuperRep(): void {
  const rep = document.querySelector('.superrep') as HTMLElement | null;
  const frames = rep && (rep.querySelector('.sr-frames') as HTMLElement | null);
  const cels = frames ? Array.from(frames.querySelectorAll('.sr-f')) as HTMLElement[] : [];
  if (!rep || !frames || cels.length < 2) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches) return;

  const rand = (min: number, max: number) => min + Math.random() * (max - min);
  const showFrame = (idx: number) => cels.forEach((c, i) => c.classList.toggle('is-on', i === idx));

  // Hand wave: one slow wave per flight. DOM order is high-open (0) -> high (1) -> mid (2) -> low (3);
  // this sequence runs it down and back up once, then rests on the raised-open pose (frame 0).
  const waveSeq = [0, 1, 2, 3, 2, 1, 0];
  const WAVE_FRAME_MS = 300; // slower cadence (~2.1s for the single wave)
  let waveTimer = 0;
  const waveOnce = () => {
    window.clearInterval(waveTimer);
    let k = 0;
    showFrame(waveSeq[0] ?? 0);
    waveTimer = window.setInterval(() => {
      k += 1;
      if (k >= waveSeq.length) { window.clearInterval(waveTimer); showFrame(0); return; }
      showFrame(waveSeq[k] ?? 0);
    }, WAVE_FRAME_MS);
  };

  let lastTop = -99;
  const pass = () => {
    const ltr = Math.random() < 0.5; // travel direction this pass
    let top = rand(6, 36); // new height band each flight...
    while (Math.abs(top - lastTop) < 9) top = rand(6, 36); // ...clearly different from the last one
    lastTop = top;
    rep.style.top = top.toFixed(1) + 'vh';
    rep.style.animation = 'none';
    // The base image faces left->right; flip the frame stack for a right->left pass so it always
    // faces its heading (waving arm stays the leading side).
    frames.classList.toggle('flip', !ltr);
    showFrame(0); // rest on the raised-open pose while crossing
    // Reflow so the re-assigned animation restarts cleanly.
    void rep.offsetWidth;
    const dur = rand(9, 15); // seconds; slower = calmer, faster = livelier
    rep.style.animation = (ltr ? 'srLTR' : 'srRTL') + ' ' + dur.toFixed(1) + 's linear';
    rep.classList.add('sr-run');
    window.setTimeout(waveOnce, dur * 1000 * 0.18); // one wave, shortly after it enters view
  };

  rep.addEventListener('animationend', (e) => {
    if ((e as AnimationEvent).animationName !== 'srLTR' && (e as AnimationEvent).animationName !== 'srRTL') return;
    rep.classList.remove('sr-run');
    rep.style.animation = 'none';
    window.setTimeout(pass, rand(1600, 5200)); // random gap between passes
  });

  window.setTimeout(pass, 1200); // let the hero paint first
}

// The floating pill nav is a light-teal glass over the dark heroes, but that loses contrast over the
// light sections below. Once the first post-hero section reaches the nav band, switch the nav to its
// dark, high-contrast .nav-solid variant (and back again when scrolled to the top).
function initNavContrast(): void {
  const nav = document.querySelector('.nav-float') as HTMLElement | null;
  const marker = document.querySelector('.strip') as HTMLElement | null;
  if (!nav || !marker) return;
  const NAV_BAND = 92; // px: roughly the bottom edge of the floating nav
  let ticking = false;
  const apply = () => {
    ticking = false;
    nav.classList.toggle('nav-solid', marker.getBoundingClientRect().top <= NAV_BAND);
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  };
  apply();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
}

// Generic cross-fading slideshow: toggles .is-active across a container's direct-child slides and its
// nav-dot buttons. Auto-advances while on screen, pauses off-screen and on hover, dots jump to a slide,
// reduced-motion → static first slide. Drives both the hero product slideshow and the enablement cards.
function initSlideshow(rootId: string, dotsId: string): void {
  const root = document.getElementById(rootId);
  const dotsWrap = document.getElementById(dotsId);
  if (!root || !dotsWrap) return;
  const slides = (Array.prototype.slice.call(root.children) as Element[]).filter(
    (n): n is HTMLElement => n instanceof HTMLElement,
  );
  const dots = Array.prototype.slice.call(dotsWrap.querySelectorAll('button')) as HTMLElement[];
  if (slides.length < 2) return;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches;

  let idx = 0;
  const show = (n: number) => {
    idx = (n + slides.length) % slides.length;
    slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
    dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
  };

  if (reduce) {
    show(0);
    dots.forEach((d, i) => d.addEventListener('click', () => show(i)));
    return;
  }

  const DWELL = 5000;
  let timer = 0;
  let hovering = false;
  let onScreen = false;
  const stop = () => { if (timer) { window.clearInterval(timer); timer = 0; } };
  const run = () => {
    stop();
    if (!onScreen || hovering) return;
    timer = window.setInterval(() => show(idx + 1), DWELL);
  };

  dots.forEach((d, i) => d.addEventListener('click', () => { show(i); run(); }));
  root.addEventListener('mouseenter', () => { hovering = true; run(); });
  root.addEventListener('mouseleave', () => { hovering = false; run(); });

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { onScreen = e.isIntersecting; run(); }); },
      { threshold: 0.35 },
    );
    io.observe(root);
  } else {
    onScreen = true;
    run();
  }
}

// Hero "live skill diagnosis" panel overlaid on the call screenshot: the animated teal progress bars
// cycle through the real STUDIO skills (registry.ts skill_names), leaving the baked Call Score 84 ring
// in the image untouched. Bars fill left→right like a download, hold, reset, then load the next group.
function initDiagCard(): void {
  const card = document.getElementById('csDiag');
  if (!card) return;
  const bars = Array.prototype.slice.call(card.querySelectorAll('.cs-bar')) as HTMLElement[];
  const verdictEl = card.querySelector('.cs-verdict') as HTMLElement | null;
  if (!bars.length || !verdictEl) return;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches;

  type Group = { verdict: string; skills: Array<[string, number]> };
  const GROUPS: Group[] = [
    { verdict: 'Above expected', skills: [['Active Listening', 90], ['Objection Handling', 85], ['Domain Expertise', 83]] },
    { verdict: 'Strong', skills: [['Problem Development', 88], ['Setting Expectations', 79], ['Next Steps', 84]] },
    { verdict: 'On track', skills: [['Moneyball Gap Identifier', 91], ['Personalization', 74], ['Competitive', 80]] },
    { verdict: 'Above expected', skills: [['Call Prep', 88], ['A-Ha Moment', 90], ['Next Steps', 79]] },
  ];

  const nm = (b: HTMLElement) => b.querySelector('.cs-nm') as HTMLElement;
  const fl = (b: HTMLElement) => b.querySelector('.cs-fl') as HTMLElement;
  const val = (b: HTMLElement) => b.querySelector('b') as HTMLElement;

  const setLabels = (g: Group) => {
    verdictEl.textContent = g.verdict;
    bars.forEach((bar, i) => {
      const sk = g.skills[i];
      if (sk) nm(bar).textContent = sk[0];
    });
  };
  const countUp = (el: HTMLElement, to: number, ms: number) => {
    let start: number | null = null;
    const step = (t: number) => {
      if (start === null) start = t;
      const p = Math.min(1, (t - start) / ms);
      el.textContent = String(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  if (reduce) {
    const g0 = GROUPS[0];
    if (g0) {
      setLabels(g0);
      bars.forEach((bar, i) => {
        const sk = g0.skills[i];
        if (!sk) return;
        fl(bar).style.width = sk[1] + '%';
        val(bar).textContent = String(sk[1]);
      });
    }
    return;
  }

  let gi = -1;
  const fill = (g: Group) => {
    setLabels(g);
    bars.forEach((bar, i) => {
      const sk = g.skills[i];
      if (!sk) return;
      fl(bar).style.width = sk[1] + '%';
      countUp(val(bar), sk[1], 1150);
    });
  };
  const resetBars = (cb: () => void) => {
    card.classList.add('reset');
    for (const b of bars) {
      fl(b).style.width = '0%';
      nm(b).style.opacity = '0';
      val(b).textContent = '0';
    }
    window.setTimeout(() => {
      for (const b of bars) nm(b).style.opacity = '';
      card.classList.remove('reset');
      cb();
    }, 480);
  };
  const next = () => {
    gi = (gi + 1) % GROUPS.length;
    const g = GROUPS[gi];
    if (g) fill(g);
    window.setTimeout(() => resetBars(next), 3200);
  };

  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    next();
  };
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            start();
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(card);
  } else {
    start();
  }
}

function initClerkSignIn(): void {
  // Start loading Clerk immediately, but DON'T wait for it before arming the links, so a click during
  // the 1-2s Clerk load never falls through to the <a href="/app"> (which, before Access is fully off a
  // browser's cache, bounces to the Cloudflare login).
  const clerkReady: Promise<ClerkGlobal | null> = loadClerk(CLERK_PUBLISHABLE_KEY).catch(() => null);

  // CAPTURE phase + stopImmediatePropagation so this wins over the legacy "Start free" lead-form modal
  // (an inline per-button click listener in index.html) — otherwise BOTH open, stacked. With Clerk on,
  // the CTAs open Clerk instead of that form; on a normal (Clerk-off) build this whole module is
  // dead-code-eliminated, so the lead-form modal keeps working untouched on production.
  document.addEventListener(
    'click',
    (e) => {
      const link = (e.target as HTMLElement | null)?.closest?.(SIGNIN_SELECTOR) as HTMLElement | null;
      if (!link) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      // "Start free" (.js-start) is a create-account intent → Sign up; "Sign in" (.nav-signin) → Sign in.
      const wantSignUp = link.classList.contains('js-start');
      void clerkReady.then((clerk) => {
        if (clerk && !clerk.user) {
          // fallbackRedirectUrl/signUpFallbackRedirectUrl are the current props (afterSignInUrl/
          // afterSignUpUrl are deprecated and log a warning).
          const opts = { fallbackRedirectUrl: '/app', signUpFallbackRedirectUrl: '/app' };
          if (wantSignUp) clerk.openSignUp(opts);
          else clerk.openSignIn(opts);
        } else {
          // Already signed in (modal would be a no-op), or Clerk unavailable (blocked/slow/timed out) →
          // navigate to /app so the click always does something (carries the session, or falls to Access).
          window.location.href = '/app';
        }
      });
    },
    { capture: true },
  );
}

// Hero orbital "gravity funnel": a dark-space canvas behind the hero copy. STUDIO is a gold core
// nestled in the heart of a warped gravity-well funnel; six role satellites (SDR/BDR/AE/SE/CSM/AM)
// orbit the mouth, each streaming data-in dots toward the core; three gold output pills emerge from
// the narrow spout ("the bucket"). Pure canvas 2D, perspective-divide projection. Honors
// prefers-reduced-motion (one static frame, no rAF) and waits for fonts so text metrics are correct.
function initOrbitHero(): void {
  const cv = document.querySelector<HTMLCanvasElement>('.orbit-canvas');
  if (!cv) return;
  const g = cv.getContext('2d');
  if (!g) return;

  const reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches);

  let W = 0,
    H = 0,
    SC = 1,
    CX = 0.6,
    HY = 0.345,
    YOFF = 0;

  function resize(): void {
    const DPR = Math.min(2, window.devicePixelRatio || 1);
    W = cv!.clientWidth;
    H = cv!.clientHeight;
    cv!.width = Math.round(W * DPR);
    cv!.height = Math.round(H * DPR);
    g!.setTransform(DPR, 0, 0, DPR, 0, 0);
    // One full-bleed canvas (W = viewport width). <=860px: copy stacks above, funnel centred below.
    // Wider: centred headline on top, copy lower-left, funnel positioned lower-RIGHT (CX 0.70).
    if (W < 860) {
      CX = 0.5;
      YOFF = 0;
      SC = Math.max(0.42, Math.min(0.66, W / 700));
      HY = H > 0 ? Math.max(0.5, Math.min(0.82, (H - 500) / H)) : 0.74;
    } else {
      CX = 0.68;
      HY = 0.4;
      YOFF = -70; // lift the whole scene so it aligns beside the copy instead of sitting low
      SC = Math.max(0.6, Math.min(1.5, W / 860));
      // Cap the scale by height so the funnel spout + output pills always stay inside the hero. 579 is
      // the projected drop from the vanishing point to the bottom of the lowest pill at SC=1 (DEPTH 24).
      const scH = H > 0 ? (H * (1 - HY) - 20 - YOFF) / 579 : SC;
      SC = Math.max(0.5, Math.min(SC, scH));
    }
  }

  // perspective-divide projection (X right, Y up, Z forward: bigger Z = farther). f scales with SC so
  // the funnel + orbit zoom together about the vanishing point (W*CX, H*HY).
  const BASE_F = 470,
    CAMH = 170;
  function project(X: number, Y: number, Z: number): { x: number; y: number; s: number } | null {
    if (Z <= 24) return null;
    const s = (BASE_F * SC) / Z;
    return { x: W * CX + X * s, y: H * HY + (CAMH - Y) * s + YOFF, s };
  }

  // gravity funnel (surface of revolution around a vertical axis at WELL)
  const WELLx = 0,
    WELLz = 400,
    RMAX = 340,
    EPS = 20,
    DEPTH = 24,
    RINGS = 26,
    SEG = 56,
    SPOKES = 36,
    RMIN = 10;
  const yDepth = (rho: number): number => -DEPTH * (RMAX / (rho + EPS) - 1); // ~0 at rim, deep near axis
  const ringRho = (i: number): number => RMAX * Math.pow((RINGS - i) / RINGS, 1.5) + RMIN;

  const ROLES = ['SDR', 'BDR', 'AE', 'SE', 'CSM', 'AM'];
  const sats = ROLES.map((r, i) => ({
    label: r,
    ang: (i / ROLES.length) * Math.PI * 2,
    R: 214,
    z: 0,
    p: null as { x: number; y: number; s: number } | null,
  }));
  const OUT = ['WHAT GOOD LOOKS LIKE', 'BENCHMARK', 'COACHING PLAYBOOKS'];
  const stars = Array.from({ length: 170 }, () => ({
    x: Math.random(),
    y: Math.random(),
    r: Math.random() * 1.3 + 0.2,
    t: Math.random() * 6,
  }));

  function glow(x: number, y: number, r: number, rgb: string, a: number): void {
    const grad = g!.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, `rgba(${rgb},${a})`);
    grad.addColorStop(1, `rgba(${rgb},0)`);
    g!.fillStyle = grad;
    g!.beginPath();
    g!.arc(x, y, r, 0, 7);
    g!.fill();
  }

  function funnel(): void {
    for (let i = 0; i < RINGS; i++) {
      const rho = ringRho(i),
        y = yDepth(rho);
      g!.beginPath();
      let on = false;
      for (let j = 0; j <= SEG; j++) {
        const th = (j / SEG) * Math.PI * 2;
        const p = project(WELLx + rho * Math.cos(th), y, WELLz + rho * Math.sin(th));
        if (!p) {
          on = false;
          continue;
        }
        if (on) g!.lineTo(p.x, p.y);
        else {
          g!.moveTo(p.x, p.y);
          on = true;
        }
      }
      g!.closePath();
      const depthF = i / RINGS;
      g!.strokeStyle = `rgba(155,155,155,${0.07 + 0.34 * depthF})`;
      g!.lineWidth = 1;
      g!.stroke();
    }
    for (let k = 0; k < SPOKES; k++) {
      const th = (k / SPOKES) * Math.PI * 2,
        cs = Math.cos(th),
        sn = Math.sin(th);
      g!.beginPath();
      let on = false;
      for (let rho = RMAX; rho >= RMIN; rho -= 10) {
        const p = project(WELLx + rho * cs, yDepth(rho), WELLz + rho * sn);
        if (!p) {
          on = false;
          continue;
        }
        if (on) g!.lineTo(p.x, p.y);
        else {
          g!.moveTo(p.x, p.y);
          on = true;
        }
      }
      g!.strokeStyle = 'rgba(155,155,155,0.10)';
      g!.lineWidth = 1;
      g!.stroke();
    }
  }

  function draw(t: number): void {
    g!.clearRect(0, 0, W, H);
    // Brand-book HUD/instrument panel ground: graphite #1E1E1E -> #121212 (matches .hero-orbit CSS).
    const bg = g!.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, '#1e1e1e');
    bg.addColorStop(1, '#121212');
    g!.fillStyle = bg;
    g!.fillRect(0, 0, W, H);
    for (const st of stars) {
      const a = 0.3 + 0.5 * Math.abs(Math.sin(t * 0.6 + st.t));
      g!.fillStyle = `rgba(230,230,228,${a * 0.5})`;
      g!.beginPath();
      g!.arc(st.x * W, st.y * H, st.r, 0, 7);
      g!.fill();
    }

    funnel();

    const cen = project(WELLx, -62, WELLz); // STUDIO nestled into the mouth of the funnel
    const spout = project(WELLx, yDepth(RMIN), WELLz); // narrow bottom of the funnel (the bucket)
    if (!cen || !spout) return;

    // outputs: gold pills just under the spout (no connector lines). Horizontal row on wide canvases;
    // stacked vertically (centred on the spout) on narrow ones so the labels never clip the edges.
    g!.textAlign = 'center';
    g!.textBaseline = 'middle';
    const pf = `600 ${Math.round(16 * Math.max(0.82, SC))}px Oswald`;
    g!.font = pf;
    const padX = 40 * SC,
      ph = 42 * SC,
      r = ph / 2;
    const pill = (cxp: number, cyp: number, w: number, label: string): void => {
      const x0 = cxp - w / 2,
        x1 = cxp + w / 2,
        y0 = cyp - ph / 2,
        y1 = cyp + ph / 2;
      g!.beginPath();
      g!.moveTo(x0 + r, y0);
      g!.arcTo(x1, y0, x1, y1, r);
      g!.arcTo(x1, y1, x0, y1, r);
      g!.arcTo(x0, y1, x0, y0, r);
      g!.arcTo(x0, y0, x1, y0, r);
      g!.closePath();
      g!.fillStyle = 'rgba(255,190,23,0.10)';
      g!.fill();
      g!.strokeStyle = 'rgba(255,190,23,0.75)';
      g!.lineWidth = 1.5;
      g!.stroke();
      g!.fillStyle = '#ffd45c';
      g!.font = pf;
      g!.fillText(label, cxp, cyp);
    };
    const pw = OUT.map((o) => g!.measureText(o).width + padX);
    // Stack the pills vertically whenever the copy stacks above the funnel (CSS flips at 860px) OR the
    // horizontal row would overflow the canvas — so they never clip at the edges in the 600–860 band.
    const rowGap = 20 * SC;
    const rowTotal = pw.reduce((a, b) => a + b, 0) + rowGap * (OUT.length - 1);
    if (W < 860 || rowTotal > W - 24) {
      // stack vertically, centred under the spout
      const vgap = ph + 12 * SC;
      let cyp = spout.y + 64 * SC + ph / 2;
      for (let i = 0; i < OUT.length; i++) {
        pill(spout.x, cyp, pw[i] ?? 0, OUT[i] ?? '');
        cyp += vgap;
      }
    } else {
      let left = spout.x - rowTotal / 2;
      const cyp = spout.y + 46 * SC + ph / 2;
      for (let i = 0; i < OUT.length; i++) {
        const w = pw[i] ?? 0;
        pill(left + w / 2, cyp, w, OUT[i] ?? '');
        left += w + rowGap;
      }
    }
    g!.textBaseline = 'alphabetic';

    // satellites orbit the mouth. Advance + project, then connectors + data-in dots (all behind).
    for (const sp of sats) {
      if (!reduce) sp.ang += 0.0016;
      const X = WELLx + Math.cos(sp.ang) * sp.R,
        Z = WELLz + Math.sin(sp.ang) * sp.R;
      sp.z = Z;
      const p = project(X, 60, Z);
      sp.p = p;
      if (!p) continue;
      g!.strokeStyle = 'rgba(155,155,155,0.22)';
      g!.lineWidth = 1.2;
      g!.beginPath();
      g!.moveTo(p.x, p.y);
      g!.lineTo(cen.x, cen.y);
      g!.stroke();
      for (let k = 0; k < 3; k++) {
        const tt = (t * 0.4 + k / 3 + sp.ang) % 1;
        const dx = p.x + (cen.x - p.x) * tt,
          dy = p.y + (cen.y - p.y) * tt;
        g!.fillStyle = `rgba(214,214,214,${0.3 + tt * 0.55})`;
        g!.beginPath();
        g!.arc(dx, dy, 2.3, 0, 7);
        g!.fill();
      }
    }

    const satSphere = (sp: (typeof sats)[number]): void => {
      const p = sp.p;
      if (!p) return;
      const rad = Math.max(7, Math.min(15, 13 * p.s));
      glow(p.x, p.y, rad * 2.4, '200,200,200', 0.26);
      g!.fillStyle = '#d6d6d6';
      g!.beginPath();
      g!.arc(p.x, p.y, rad, 0, 7);
      g!.fill();
      g!.fillStyle = '#f6f6f3';
      g!.font = `700 ${Math.round(18 * Math.max(0.82, SC))}px Oswald`;
      g!.textAlign = 'center';
      g!.fillText(sp.label, p.x, p.y - rad - 7);
    };

    // Depth order: satellites on the FAR side of the core (Z >= WELLz) draw behind it; the STUDIO core;
    // then satellites on the NEAR side (Z < WELLz) draw ON TOP, so they pass in front as they orbit.
    for (const sp of sats) if (sp.z >= WELLz) satSphere(sp);

    const cr = 45 * Math.max(0.78, SC);
    const gr = g!.createRadialGradient(cen.x - 16, cen.y - 18, 4, cen.x, cen.y, cr + 5);
    gr.addColorStop(0, '#fff2c0');
    gr.addColorStop(0.5, '#ffbe17');
    gr.addColorStop(1, '#b47d06');
    g!.fillStyle = gr;
    g!.beginPath();
    g!.arc(cen.x, cen.y, cr, 0, 7);
    g!.fill();
    g!.fillStyle = '#1a1407';
    g!.font = `400 ${Math.round(26 * Math.max(0.78, SC))}px 'Bebas Neue',Oswald`;
    g!.textAlign = 'center';
    g!.fillText('STUDIO', cen.x, cen.y + 9 * Math.max(0.78, SC));

    for (const sp of sats) if (sp.z < WELLz) satSphere(sp);
  }

  resize();
  window.addEventListener('resize', resize);

  if (reduce) {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    (fonts?.ready ?? Promise.resolve()).then(() => {
      resize();
      draw(0);
    });
    return;
  }

  const t0 = performance.now();
  function loop(): void {
    draw((performance.now() - t0) / 1000);
    requestAnimationFrame(loop);
  }
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  (fonts?.ready ?? Promise.resolve()).then(resize);
  requestAnimationFrame(loop);
}
